import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QB_EMBED_PROBE_GRACE_MS, QB_EMBED_PROBE_TIMEOUT_MS } from '../quickbase-types';
import { isEmbedAuthedMessage, safeOrigin } from '../quickbase-auth/quickbase-auth-complete';
import { quickbaseAuthServiceSingleton } from '../quickbase-auth';
import { QuickbaseHostBridge } from '../quickbase-host-bridge/quickbase-host-bridge';
import Button from '../../../src/components/library/Button';
import { LoaderWrapper } from '../../../src/components/library/LoaderWrapper';
import { ButtonRow, ErrorContainer, Hint, PanelTitle, StyledIframe } from './quickbase-iframe.styles';

export const QB_IFRAME_SANDBOX =
	'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

export interface VerifiedQuickbaseEmbedProps {
	embedUrl: string;
	frameUrl?: string | null;
	iFrameType: string;
	recoverSession?(): Promise<boolean>;
	probeTimeoutMs?: number;
	probeGraceMs?: number;
	bridge?: QuickbaseHostBridge | null;
	reloadToken?: number;
}

type EmbedStatus = 'probing' | 'connected' | 'failed';

/**
 * Verified flow: the frame loads the return page in probe mode behind a loader. Framed with a session,
 * the code page posts `authed` and stays put, and this component swaps the frame to the real page. No
 * message means Quickbase bounced the frame to sign-in, so the store recovers via a silent SSO redirect.
 */
export function VerifiedQuickbaseEmbed({
	embedUrl,
	frameUrl,
	iFrameType,
	recoverSession,
	probeTimeoutMs = QB_EMBED_PROBE_TIMEOUT_MS,
	probeGraceMs = QB_EMBED_PROBE_GRACE_MS,
	bridge = null,
	reloadToken = 0,
}: VerifiedQuickbaseEmbedProps) {
	const probeUrl = frameUrl ?? embedUrl;
	const canProbe = probeUrl !== embedUrl;
	const probeOrigin = useMemo(() => safeOrigin(probeUrl), [probeUrl]);

	// `attempt` bumps on a manual retry to force a fresh probe even when the URL is unchanged.
	const [attempt, setAttempt] = useState(0);
	const initialStatus: EmbedStatus = canProbe ? 'probing' : 'connected';
	const [status, setStatus] = useState<EmbedStatus>(initialStatus);
	// A new probe cycle (URL change, manual retry, or host-bridge reload) resets the status during render,
	// so the first render of the new cycle already mounts the probe rather than the real page.
	const cycle = `${embedUrl}#${probeUrl}#${attempt}#${reloadToken}`;
	const [statusCycle, setStatusCycle] = useState(cycle);
	if (statusCycle !== cycle) {
		setStatusCycle(cycle);
		setStatus(initialStatus);
	}
	const cycleRef = useRef(cycle);
	cycleRef.current = cycle;
	const statusRef = useRef(status);
	statusRef.current = status;
	const graceTimer = useRef<ReturnType<typeof setTimeout>>();
	// Guards against the grace and hard-timeout timers both triggering a recovery for one probe cycle.
	const recoveringRef = useRef(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const src = status === 'probing' ? probeUrl : embedUrl;

	const clearCooldown = () => quickbaseAuthServiceSingleton.get().clearRecoveryCooldown();

	// Drop the grace timer and recovery guard left over from the previous cycle so they can never act on
	// the new probe. The hard timeout is keyed on `cycle` below for the same reason.
	useEffect(() => {
		clearTimeout(graceTimer.current);
		recoveringRef.current = false;
	}, [cycle]);

	const onProbeFailed = useCallback(async () => {
		if (statusRef.current !== 'probing' || recoveringRef.current) return;
		const failedCycle = cycleRef.current;
		recoveringRef.current = true;
		const recovering = recoverSession ? await recoverSession() : false;
		if (cycleRef.current !== failedCycle) return;
		recoveringRef.current = false;
		// When recovering, the page redirects and unloads; keep the loader until it does.
		if (!recovering) setStatus('failed');
	}, [recoverSession]);

	// Attach the listener regardless of status so a late "authed" is never missed.
	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			if (
				event.origin === probeOrigin &&
				event.source === iframeRef.current?.contentWindow &&
				isEmbedAuthedMessage(event.data)
			) {
				clearTimeout(graceTimer.current);
				clearCooldown();
				setStatus('connected');
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [probeOrigin]);

	useEffect(() => {
		if (status !== 'probing') return undefined;
		const timer = setTimeout(onProbeFailed, probeTimeoutMs);
		return () => clearTimeout(timer);
	}, [status, cycle, probeTimeoutMs, onProbeFailed]);

	useEffect(() => () => clearTimeout(graceTimer.current), []);

	// Host-bridge deep-linking: attach only once the real page is showing, and re-attach when the frame
	// remounts (probe -> real swap, retry, or reload) since each remount is a fresh iframe element.
	useEffect(() => {
		if (!bridge || status !== 'connected' || !iframeRef.current) return undefined;
		return bridge.attach(iframeRef.current);
	}, [bridge, status, src]);

	// The probe page posts before its load event; a load with no message means Quickbase bounced the frame.
	const handleFrameLoad = () => {
		if (statusRef.current !== 'probing') return;
		clearTimeout(graceTimer.current);
		graceTimer.current = setTimeout(onProbeFailed, probeGraceMs);
	};

	const tryAgain = () => {
		clearCooldown();
		setAttempt((n) => n + 1);
	};

	const openInNewTab = () => {
		window.open(embedUrl, '_blank', 'noopener,noreferrer');
	};

	if (status === 'failed') {
		return (
			<ErrorContainer role="alert">
				<PanelTitle>This page could not be loaded</PanelTitle>
				<Hint>The embedded content did not respond. Your browser may be blocking third-party cookies for it.</Hint>
				<ButtonRow>
					<Button nxstyle="primary-blue" onClick={tryAgain}>
						Try again
					</Button>
					<Button nxstyle="normal" onClick={openInNewTab}>
						Open in a new tab
					</Button>
				</ButtonRow>
			</ErrorContainer>
		);
	}

	return (
		<LoaderWrapper loading={status === 'probing'} style={{ height: '100%', width: '100%' }}>
			<StyledIframe
				ref={iframeRef}
				key={`${src}#${attempt}#${reloadToken}`}
				src={src}
				sandbox={QB_IFRAME_SANDBOX}
				onLoad={handleFrameLoad}
				title={`${iFrameType} Quickbase IFrame`}
			/>
		</LoaderWrapper>
	);
}
